import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress, Chip,
  Avatar, Divider, Select, MenuItem, FormControl, InputLabel, Button,
  Paper, LinearProgress, TextField, InputAdornment, Tab, Tabs, Tooltip,
  IconButton, Stack, Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  RefreshOutlined as RefreshIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  School as SchoolIcon,
  CalendarMonth as CalendarIcon,
  AccessTimeOutlined as ClockIcon,
  CheckCircleOutline as CheckIcon,
  WarningAmberOutlined as WarnIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MenuBook as SubjectIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, Cell, Legend, CartesianGrid,
} from 'recharts';
import LocationOnIcon from '@mui/icons-material/LocationOn';

// ─────────────────────────────────────────────────────────────────────────────
// Constants & helpers
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  primary: '#0B1F3D',
  navy:    '#1a3a6b',
  accent:  '#1976D2',
  green:   '#2E7D32',
  orange:  '#E65100',
  red:     '#C62828',
  yellow:  '#F57F17',
  border:  '#E2E8F0',
  surface: '#F8FAFC',
  muted:   '#64748B',
};

const ROW_OPTIONS = [5, 10, 15, 25];

const apiUrl   = process.env.REACT_APP_BASE_URL;
const getToken = () => sessionStorage.getItem('token');
const authH    = () => ({ Authorization: getToken() });
const postH    = () => ({ 'Content-Type': 'application/json', Authorization: getToken() });

// Day order for sorting
const DAY_ORDER = { Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6, Sunday:7 };

// Soft pastel palette for subject cards
const SUBJECT_PALETTES = [
  { bg: '#EDE7F6', border: '#9575CD', text: '#4527A0' },
  { bg: '#E3F2FD', border: '#64B5F6', text: '#0D47A1' },
  { bg: '#E8F5E9', border: '#66BB6A', text: '#1B5E20' },
  { bg: '#FFF3E0', border: '#FFB74D', text: '#BF360C' },
  { bg: '#FCE4EC', border: '#F48FB1', text: '#880E4F' },
  { bg: '#E0F7FA', border: '#4DD0E1', text: '#006064' },
  { bg: '#F9FBE7', border: '#DCE775', text: '#558B2F' },
  { bg: '#FBE9E7', border: '#FF8A65', text: '#BF360C' },
  { bg: '#E8EAF6', border: '#7986CB', text: '#1A237E' },
  { bg: '#F3E5F5', border: '#BA68C8', text: '#4A148C' },
];
const subjectPaletteCache = {};
let paletteIdx = 0;
const getPalette = (name) => {
  if (!subjectPaletteCache[name]) {
    subjectPaletteCache[name] = SUBJECT_PALETTES[paletteIdx % SUBJECT_PALETTES.length];
    paletteIdx++;
  }
  return subjectPaletteCache[name];
};


// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────────────────────

const StatusChip = ({ status }) => {
  const map = {
    'On Track': { bg: '#E8F5E9', color: C.green  },
    Met:        { bg: '#E8F5E9', color: C.green  },
    Over:       { bg: '#FFF3E0', color: C.orange },
    Behind:     { bg: '#FFEBEE', color: C.red    },
    Under:      { bg: '#FFEBEE', color: C.red    },
    Normal:     { bg: '#E3F2FD', color: C.accent },
    Low:        { bg: '#FFF8E1', color: C.yellow },
    High:       { bg: '#FCE4EC', color: '#AD1457' },
    OK:         { bg: '#E8F5E9', color: C.green  },
  };
  const s = map[status] || { bg: '#F1F5F9', color: C.muted };
  return (
    <Chip
      label={status || '—'}
      size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: '0.7rem', borderRadius: 1.5, height: 22 }}
    />
  );
};

const UtilBar = ({ value }) => {
  const pct  = Math.min(value || 0, 100);
  const fill = pct >= 70 ? C.green : pct >= 30 ? C.accent : C.yellow;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          flex: 1, height: 7, borderRadius: 7,
          bgcolor: '#EEF2F7',
          '& .MuiLinearProgress-bar': { borderRadius: 7, bgcolor: fill },
        }}
      />
      <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 38, color: C.primary, textAlign: 'right' }}>
        {pct.toFixed(1)}%
      </Typography>
    </Box>
  );
};

const TablePager = ({ total, page, rowsPerPage, onPage, onRows }) => {
  const pages = Math.max(Math.ceil(total / rowsPerPage), 1);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 2, py: 1.2, borderTop: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">Rows per page:</Typography>
        <Select
          value={rowsPerPage}
          onChange={e => { onRows(Number(e.target.value)); onPage(1); }}
          size="small"
          sx={{ fontSize: '0.78rem', height: 28, minWidth: 62 }}
        >
          {ROW_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {Math.min((page - 1) * rowsPerPage + 1, total)}–{Math.min(page * rowsPerPage, total)} of {total}
        </Typography>
        <IconButton size="small" onClick={() => onPage(page - 1)} disabled={page <= 1}>
          <PrevIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onPage(page + 1)} disabled={page >= pages}>
          <NextIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const SearchBar = ({ value, onChange, placeholder = 'Search…', width = 280 }) => (
  <TextField
    size="small"
    placeholder={placeholder}
    value={value}
    onChange={e => onChange(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ fontSize: 17, color: '#94A3B8' }} />
        </InputAdornment>
      ),
    }}
    sx={{ width, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
  />
);

const TH = ({ children, align = 'left', width }) => (
  <TableCell
    align={align}
    sx={{
      bgcolor: '#F1F5F9', fontWeight: 700, fontSize: '0.75rem',
      color: '#475569', py: 1.3, letterSpacing: 0.2,
      ...(width ? { width } : {}),
    }}
  >
    {children}
  </TableCell>
);

// ─────────────────────────────────────────────────────────────────────────────
// Subject card used in Teacher Detail tab
// Each card = one unique timetable slot (deduplicated by timetableId),
// expanded to show every unique dayName+periodId combo for that subject+class
// ─────────────────────────────────────────────────────────────────────────────
const DayBadge = ({ day }) => {
  const short = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };
  return (
    <Box sx={{
      px: 1, py: 0.3, borderRadius: 1, bgcolor: C.primary + '15',
      border: `1px solid ${C.primary}30`,
    }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: C.primary }}>
        {short[day] || day}
      </Typography>
    </Box>
  );
};

const SubjectCard = ({ group }) => {

  const [open, setOpen] = useState(false);
  const pal = getPalette(group.subjectName);

  // sort slots by day order then periodSeqNo
  const sortedSlots = [...group.slots].sort((a, b) => {
    const dDiff = (DAY_ORDER[a.dayName] || 99) - (DAY_ORDER[b.dayName] || 99);
    return dDiff !== 0 ? dDiff : (a.periodSeqNo - b.periodSeqNo);
  });

  const uniqueDays = [...new Set(sortedSlots.map(s => s.dayName))];

  return (
    <Card sx={{
      border: `1.5px solid ${pal.border}40`,
      borderLeft: `4px solid ${pal.border}`,
      borderRadius: 2.5,
      boxShadow: 'none',
      bgcolor: pal.bg,
      transition: 'box-shadow 0.18s',
      '&:hover': { boxShadow: `0 4px 18px ${pal.border}50` },
    }}>
      <CardContent sx={{ p: '14px !important' }}>
        {/* ── Card header ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
              <SubjectIcon sx={{ fontSize: 16, color: pal.text }} />
              <Typography sx={{ fontWeight: 800, color: pal.text, fontSize: '0.92rem', lineHeight: 1.2 }}>
                {group.subjectName}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600 }}>
              {group.className} · {group.sectionName}
            </Typography>
          </Box>
          {/* Period count badge */}
          <Box sx={{
            ml: 1, px: 1.5, py: 0.5, borderRadius: 2,
            bgcolor: pal.text + '18', flexShrink: 0,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
          }}>
            <Typography sx={{ fontWeight: 900, color: pal.text, fontSize: '1.1rem', lineHeight: 1 }}>
              {group.slots.length}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: pal.text, fontWeight: 600, letterSpacing: 0.3 }}>
              PERIODS
            </Typography>
          </Box>
        </Box>

        {/* ── Day badges summary ── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.2 }}>
          {uniqueDays.map(d => <DayBadge key={d} day={d} />)}
        </Box>

        {/* ── Time info from first slot ── */}
        {sortedSlots[0] && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <ClockIcon sx={{ fontSize: 13, color: C.muted }} />
            <Typography variant="caption" sx={{ color: C.muted }}>
              {sortedSlots[0].startTime} – {sortedSlots[0].endTime}
            </Typography>
          </Box>
        )}

        {/* ── Expand/collapse detailed slot list ── */}
        <Button
          size="small"
          endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setOpen(!open)}
          sx={{
            px: 1, py: 0.3, fontSize: '0.72rem', fontWeight: 700,
            color: pal.text, bgcolor: pal.text + '12',
            borderRadius: 1.5, minWidth: 0, textTransform: 'none',
            '&:hover': { bgcolor: pal.text + '22' },
          }}
        >
          {open ? 'Hide slots' : `View ${sortedSlots.length} slot${sortedSlots.length !== 1 ? 's' : ''}`}
        </Button>

        <Collapse in={open}>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {sortedSlots.map((slot, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: '#fff', borderRadius: 1.5, px: 1.2, py: 0.6,
                  border: `1px solid ${pal.border}30`,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: pal.border, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: C.primary, minWidth: 56 }}>
                  {slot.dayName?.slice(0,3)}
                </Typography>
                <Typography variant="caption" sx={{ color: C.muted, flex: 1 }}>
                  Period {slot.periodSeqNo} &nbsp;·&nbsp; {slot.startTime} – {slot.endTime}
                </Typography>
                {(slot.isProxy === 'Yes') && (
  <Tooltip title={`Proxy for ${slot.originalTeacherName}`}>
    <Chip label="Proxy" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 700 }} />
  </Tooltip>
)}
              </Box>
            ))}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const getDefaultFromDate = () => {
  const d = new Date();
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d.toISOString().split('T')[0];
};

const getDefaultToDate = () => getSaturdayOfWeek(getDefaultFromDate());

const getSaturdayOfWeek = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0=Sun,1=Mon,...6=Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday + 5); // Monday + 5 = Saturday
  return d.toISOString().split('T')[0];
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function TeacherLoadReport() {
  const [tab, setTab] = useState(0);

  // Tab 0
  const [utilData,    setUtilData]    = useState([]);
  const [loadingUtil, setLoadingUtil] = useState(true);
  const [utilSearch,  setUtilSearch]  = useState('');
  const [utilPage,    setUtilPage]    = useState(1);
  const [utilRows,    setUtilRows]    = useState(10);

  // Tab 1
  const [summaryList,   setSummaryList]   = useState([]);
  const [rawDetail,     setRawDetail]     = useState([]);   // raw rows from /detail
  const [selTeacher,    setSelTeacher]    = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [cardSearch,    setCardSearch]    = useState('');
  const [cardPage,      setCardPage]      = useState(1);
  const [cardsPerPage,  setCardsPerPage]  = useState(10);

  // Tab 2
  const [classes,      setClasses]      = useState([]);
  const [sections,     setSections]     = useState([]);
  const [selClass,     setSelClass]     = useState('');
  const [selSection,   setSelSection]   = useState('');
  const [valRows,      setValRows]      = useState([]);
  const [ttSlots,      setTtSlots]      = useState([]);
  const [loadingClass, setLoadingClass] = useState(false);
  const [classSearch,  setClassSearch]  = useState('');
  const [classPage,    setClassPage]    = useState(1);
  const [classRows,    setClassRows]    = useState(10);

const [refreshKey, setRefreshKey] = useState(0);
const [repFromDate, setRepFromDate] = useState(getDefaultFromDate());
const [repToDate,   setRepToDate]   = useState(getDefaultToDate());

  // ── Branch state (mirrors Employee.js pattern) ──────────────────────────
  const [isMaster,        setIsMaster]        = useState(false);
  const [branches,        setBranches]        = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // ── Resolve active branchId for use in all API calls ──────────────────
  // Master users: from dropdown selection
  // Non-master users: from sessionStorage (set at login)
  const activeBranchId = isMaster
    ? selectedBranchId
    : sessionStorage.getItem('branchId');

  // ── Fetch branch list (master users only) ─────────────────────────────
  const fetchBranches = async () => {
    try {
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: 'POST',
        headers: postH(),
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        // Auto-fill first branch for master user when branches load
        if (data.length > 0) {
          setSelectedBranchId(data[0].branchId);
        }
      } else {
        console.error('Failed to fetch branches');
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
  if (repFromDate) {
    setRepToDate(getSaturdayOfWeek(repFromDate));
  }
}, [repFromDate]);

  // ── On mount: detect master user, load branches or read from session ──
  useEffect(() => {
    const isMasterUser = sessionStorage.getItem('is_Master') === 'true';
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      // fetchBranches sets selectedBranchId → triggers the main data load
      fetchBranches();
    } else {
      // Non-master: branchId already in session, activeBranchId resolves it
      // Trigger initial load via refreshKey effect below
    }
  }, []);

  // ── Init: load utilization + summary + classes ────────────────────────
  // Runs on mount (for non-master) and whenever branch changes or refresh is triggered.
  // For master users, waits until selectedBranchId is set by fetchBranches().
  useEffect(() => {
    // Guard: don't fetch until we have a valid branchId
    if (!activeBranchId) return;

    const load = async () => {
      setLoadingUtil(true);
      
      try {
       const utilParams    = new URLSearchParams({ Branch_id: activeBranchId, fromDate: repFromDate, toDate: repToDate });
        const summaryParams = new URLSearchParams({ Branch_id: activeBranchId, fromDate: repFromDate, toDate: repToDate });

        const [uRes, sRes, cRes] = await Promise.all([
          // GET /adv/teacher-load/utilization — Branch_id + fromDate + toDate as query params
          fetch(`${apiUrl}/adv/teacher-load/utilization?${utilParams}`, { headers: authH() }),
          // GET /adv/teacher-load/summary — Branch_id + fromDate + toDate as query params
          fetch(`${apiUrl}/adv/teacher-load/summary?${summaryParams}`, { headers: authH() }),
          // POST /Enquiry/ddlClassName — no parameters (Swagger: No parameters, send empty body)
          fetch(`${apiUrl}/Enquiry/ddlClassName`, {
            method: 'POST', headers: postH(),
            body: JSON.stringify({}),
          }),
        ]);
        setUtilData(   await uRes.json().then(d => Array.isArray(d) ? d : []));
        setSummaryList(await sRes.json().then(d => Array.isArray(d) ? d : []));
        setClasses(    await cRes.json().then(d => Array.isArray(d) ? d : []));
      } catch (e) { console.error(e); }
      finally { setLoadingUtil(false); }
    };
    load();
  // Re-run when branch changes (selectedBranchId for master, refreshKey for manual refresh)
}, [selectedBranchId, refreshKey, repFromDate, repToDate]);
  // ── Teacher detail ────────────────────────────────────────────────────────
useEffect(() => {
    if (!selTeacher || !activeBranchId) return;
    const load = async () => {
      setLoadingDetail(true);
      setRawDetail([]); setCardSearch(''); setCardPage(1);
      try {
        const params = new URLSearchParams({ teacherId: selTeacher, Branch_id: activeBranchId });
        if (repFromDate) params.set('fromDate', repFromDate);
        if (repToDate)   params.set('toDate',   repToDate);
        const res = await fetch(`${apiUrl}/adv/teacher-load/detail?${params}`, { headers: authH() });
        setRawDetail(await res.json().then(d => Array.isArray(d) ? d : []));
      } catch (e) { console.error(e); }
      finally { setLoadingDetail(false); }
    };
    load();
}, [selTeacher, repFromDate, repToDate]);

  // ── Sections ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selClass || !activeBranchId) return;
    const load = async () => {
      try {
        // POST /Teacher/ddlSection_clsId — body: { teacherId, classId } only (Swagger: no branchId)
        const res = await fetch(`${apiUrl}/Teacher/ddlSection_clsId`, {
          method: 'POST', headers: postH(),
          body: JSON.stringify({
            teacherId: sessionStorage.getItem('employeeId') || 0,
            classId: selClass,
          }),
        });
        setSections(await res.json().then(d => Array.isArray(d) ? d : []));
      } catch {}
      setSelSection(''); setValRows([]); setTtSlots([]);
    };
    load();
  }, [selClass]);

  // ── Class subject data ────────────────────────────────────────────────────
  useEffect(() => {
    if (!selClass || !selSection || !activeBranchId) return;
    const load = async () => {
      setLoadingClass(true); setClassSearch(''); setClassPage(1);
      try {
       const validationParams = new URLSearchParams({ classId: selClass, sectionId: selSection, Branch_id: activeBranchId, fromDate: repFromDate, toDate: repToDate });
        const classViewParams  = new URLSearchParams({ classId: selClass, sectionId: selSection, Branch_id: activeBranchId, fromDate: repFromDate, toDate: repToDate });

        const [vRes, ttRes] = await Promise.all([
          // GET /adv/subject-target/validation — classId + sectionId + Branch_id + fromDate + toDate
          fetch(`${apiUrl}/adv/subject-target/validation?${validationParams}`, { headers: authH() }),
          // GET /adv/timetable/class-view — classId + sectionId + Branch_id + fromDate + toDate
          fetch(`${apiUrl}/adv/timetable/class-view?${classViewParams}`,      { headers: authH() }),
        ]);
        setValRows(  await vRes.json().then( d => Array.isArray(d) ? d : []));
        setTtSlots(  await ttRes.json().then(d => Array.isArray(d) ? d : []));
      } catch (e) { console.error(e); }
      finally { setLoadingClass(false); }
    };
    load();
  }, [selSection, repFromDate, repToDate]);

  // ══════════════════════════════════════════════════════════════════════════
  // DERIVED DATA
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * DETAIL DEDUPLICATION STRATEGY:
   * The API returns duplicate rows for every timetableId (backend bug/design).
   * Step 1 — deduplicate by timetableId → gives us true unique slots.
   * Step 2 — group unique slots by (subjectId + classId + sectionId) → card groups.
   * Each card group then lists all the actual periods for that subject.
   */
  const subjectGroups = useMemo(() => {

   const seenIds = new Set();
const unique = rawDetail.filter(r => {
  const dedupeKey = `${r.timetableId}_${r.workDate || r.WorkDate || ''}`;
  if (seenIds.has(dedupeKey)) return false;
  seenIds.add(dedupeKey);
  return true;
});

    // Step 2: group by subject + class + section
    const map = {};
    unique.forEach(r => {
      const key = `${r.subjectId}_${r.classId}_${r.sectionId}`;
      if (!map[key]) {
        map[key] = {
          key,
          subjectId:   r.subjectId,
          subjectName: r.subjectName,
          classId:     r.classId,
          className:   r.className,
          sectionId:   r.sectionId,
          sectionName: r.sectionName,
          slots: [],
        };
      }
     map[key].slots.push({
  timetableId: r.timetableId,
  dayName:     r.dayName,
  daysId:      r.daysId,
  periodId:    r.periodId,
  periodSeqNo: r.periodSeqNo,
  startTime:   r.startTime,
  endTime:     r.endTime,
  workDate:        r.workDate ?? r.WorkDate,
  isProxy:         r.isProxy  ?? r.IsProxy,
  originalTeacherName: r.originalTeacherName ?? r.OriginalTeacherName,
  proxyTeacherName:    r.proxyTeacherName    ?? r.ProxyTeacherName,
});
    });

    return Object.values(map).sort((a, b) =>
      a.className.localeCompare(b.className) || a.subjectName.localeCompare(b.subjectName)
    );
  }, [rawDetail]);

  // Unique timetable slot count (deduplicated)
  const uniqueSlotCount = useMemo(() => {
    const seen = new Set();
    rawDetail.forEach(r => seen.add(r.timetableId));
    return seen.size;
  }, [rawDetail]);

  const filteredGroups = useMemo(() =>
    subjectGroups.filter(g =>
      !cardSearch ||
      g.subjectName?.toLowerCase().includes(cardSearch.toLowerCase()) ||
      g.className?.toLowerCase().includes(cardSearch.toLowerCase()) ||
      g.sectionName?.toLowerCase().includes(cardSearch.toLowerCase())
    ), [subjectGroups, cardSearch]);

  const pagedGroups = useMemo(() =>
    filteredGroups.slice((cardPage - 1) * cardsPerPage, cardPage * cardsPerPage),
    [filteredGroups, cardPage, cardsPerPage]);

  // Tab 0 filtered
  const filteredUtil = useMemo(() =>
    utilData.filter(t =>
      !utilSearch || t.teacherName?.toLowerCase().includes(utilSearch.toLowerCase())
    ), [utilData, utilSearch]);

  const pagedUtil = useMemo(() =>
    filteredUtil.slice((utilPage - 1) * utilRows, utilPage * utilRows),
    [filteredUtil, utilPage, utilRows]);

  // Tab 2 merged
  const mergedClass = useMemo(() => {
    const teacherMap = {};
    // deduplicate timetable slots by timetableId for teacher lookup
    const seenTt = new Set();
    ttSlots.forEach(s => {
      if (!seenTt.has(s.timetableId)) {
        seenTt.add(s.timetableId);
        teacherMap[s.subjectId] = s.teacherName;
      }
    });
    return valRows.map(v => ({
      subjectId:    v.subjectId,
      subjectName:  v.subjectName,
      targetPeriods:v.targetPeriods,
      actualPeriods:v.actualPeriods,
      difference:   v.difference,
      status:       v.status,
      teacherName:  teacherMap[v.subjectId] || '—',
    }));
  }, [valRows, ttSlots]);

  const filteredClass = useMemo(() =>
    mergedClass.filter(r =>
      !classSearch ||
      r.subjectName?.toLowerCase().includes(classSearch.toLowerCase()) ||
      r.teacherName?.toLowerCase().includes(classSearch.toLowerCase())
    ), [mergedClass, classSearch]);

  const pagedClass = useMemo(() =>
    filteredClass.slice((classPage - 1) * classRows, classPage * classRows),
    [filteredClass, classPage, classRows]);

  // Charts
  const utilChartData = useMemo(() =>
    [...utilData]
      .sort((a, b) => b.utilizationPercent - a.utilizationPercent)
      .slice(0, 12)
      .map(t => ({
        name:    t.teacherName?.split(' ')[0],
        util:    parseFloat((t.utilizationPercent || 0).toFixed(2)),
        periods: t.assignedPeriods || 0,
      })),
    [utilData]);

  const classChartData = useMemo(() =>
    mergedClass.map(r => ({
      name:   r.subjectName?.length > 10 ? r.subjectName.slice(0,9)+'…' : r.subjectName,
      Target: r.targetPeriods,
      Actual: r.actualPeriods,
    })),
    [mergedClass]);

  const selTeacherSummary = useMemo(() =>
    summaryList.find(t => t.teacherId === Number(selTeacher)),
    [summaryList, selTeacher]);

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ bgcolor: C.surface, minHeight: '100vh', pb: 5 }}>

      {/* Page header */}
      <Box sx={{
        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.navy} 100%)`,
        py: 3.5, px: { xs: 2, md: 4 }, mb: 3,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: -0.5 }}>
            Teacher Load Reports
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.3 }}>
            Utilization analysis · Per-teacher slot detail · Class subject tracking
          </Typography>
        </Box>

        {/* Right: Branch Dropdown (master users only) + Refresh button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

          {/* ── Branch Dropdown — visible only for Master/Admin users ── */}
          {isMaster && (
            <FormControl size="small" sx={{ width: 220 }}>
              <InputLabel
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.6px',
                  textTransform: 'uppercase',
                  top: '-2px',
                  '&.Mui-focused': { color: '#64B5F6' },
                  '&.MuiFormLabel-filled': { color: 'rgba(255,255,255,0.7)' },
                }}
              >
                Branch
              </InputLabel>
              <Select
                value={selectedBranchId || ''}
                onChange={(e) => {
  setSelectedBranchId(e.target.value);
  setUtilSearch(''); setUtilPage(1);
  setCardSearch(''); setCardPage(1);
  setClassSearch(''); setClassPage(1);
  // Also clear cross-tab data when branch changes
  setSelTeacher(''); setRawDetail([]);
  setSelClass(''); setSelSection(''); setValRows([]); setTtSlots([]);
}}
                label="Branch"
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
                  </InputAdornment>
                }
                sx={{
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  letterSpacing: '0.3px',
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.15)',
                    borderWidth: '1px',
                    transition: 'border-color 0.2s ease',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.11)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.35)',
                    },
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#64B5F6',
                      borderWidth: '1.5px',
                    },
                  },
                  '& .MuiSelect-icon': {
                    color: 'rgba(255,255,255,0.5)',
                    transition: 'color 0.2s ease',
                  },
                  '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
                  '& .MuiSelect-select': { py: 1.1 },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 0.5,
                      borderRadius: '10px',
                      border: '1px solid rgba(11,31,61,0.12)',
                      boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                      overflow: 'hidden',
                      '& .MuiList-root': { py: 0.5 },
                    },
                  },
                }}
              >
                {branches.map((branch) => (
                  <MenuItem
                    key={branch.branchId}
                    value={branch.branchId}
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#0B1F3D',
                      px: 2, py: 1.1, mx: 0.5,
                      borderRadius: '6px',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25,118,210,0.1)',
                        color: '#1565C0',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                      },
                    }}
                  >
                    {branch.branchName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Refresh button — respects current branch via activeBranchId in useEffect */}
{/* ── Date Range Filter ── */}
          <TextField
            label="From" type="date" size="small"
            value={repFromDate}
            onChange={e => setRepFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)', color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
              '& input': { color: '#fff' },
            }}
          />
         <TextField
  label="To" type="date" size="small"
  value={repToDate}
  disabled
  InputLabelProps={{ shrink: true }}
  sx={{
    width: 150,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.07)', color: '#fff',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
      '&.Mui-disabled fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
    '& .MuiInputLabel-root.Mui-disabled': { color: 'rgba(255,255,255,0.6)' },
    '& input': { color: '#fff', WebkitTextFillColor: '#fff' },
    '& .MuiInputBase-input.Mui-disabled': { color: '#fff', WebkitTextFillColor: '#fff' },
  }}
/>

          {/* Refresh button — respects current branch via activeBranchId in useEffect */}
          <Tooltip title="Refresh all">            <IconButton
              onClick={() => setRefreshKey(k => k + 1)}
              sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(11,31,61,0.09)', border: `1px solid ${C.border}` }}>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: `1px solid ${C.border}`, px: 2, pt: 1,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 50, fontSize: '0.88rem' },
              '& .Mui-selected': { color: C.primary },
              '& .MuiTabs-indicator': { bgcolor: C.primary, height: 3, borderRadius: 2 },
            }}
          >
            <Tab label="Utilization Report"    icon={<TrendingUpIcon />} iconPosition="start" />
            <Tab label="Teacher Detail"        icon={<PersonIcon />}    iconPosition="start" />
            <Tab label="Class Subject Summary" icon={<SchoolIcon />}    iconPosition="start" />
          </Tabs>

          {/* ================================================================
              TAB 0 — UTILIZATION
          ================================================================ */}
          {tab === 0 && (
            <>
              {loadingUtil ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress sx={{ color: C.primary }} />
                </Box>
              ) : (
                <>
                  {/* KPI row */}
                  <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Total Teachers',   value: utilData.length,  sub: `${utilData.filter(t=>t.assignedPeriods>0).length} active`, color: C.accent },
                        { label: 'Avg Utilization',  value: utilData.length ? `${(utilData.reduce((s,t)=>s+(t.utilizationPercent||0),0)/utilData.length).toFixed(1)}%` : '0%', sub: 'Across all teachers', color: C.green },
                        { label: 'Low Utilization',  value: utilData.filter(t=>t.loadCategory==='Low').length, sub: 'Needs attention', color: C.yellow },
                        { label: 'Unassigned',       value: utilData.filter(t=>(t.assignedPeriods||0)===0).length, sub: '0 periods assigned', color: C.muted },
                      ].map((kpi, i) => (
                        <Grid item xs={6} md={3} key={i}>
                          <Paper sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${C.border}`, boxShadow: 'none' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.67rem' }}>
                              {kpi.label}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: kpi.color, lineHeight: 1.2, mt: 0.3 }}>
                              {kpi.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{kpi.sub}</Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* Chart */}
                  <Box sx={{ px: 2.5, pb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.primary, mb: 1.5 }}>
                      Assigned Periods & Utilization — Top 12 Teachers
                    </Typography>
                    <Box sx={{ height: 210 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={utilChartData} margin={{ left: -15, right: 5, top: 5, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis yAxisId="left"  orientation="left"  tick={{ fontSize: 10 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} domain={[0,100]} tickFormatter={v=>`${v}%`} />
                          <RTooltip formatter={(v, n) => n === 'util' ? [`${v}%`, 'Utilization'] : [v, 'Periods']} />
                          <Legend />
                          <Bar yAxisId="left"  dataKey="periods" name="Assigned Periods" fill="#90CAF9" radius={[3,3,0,0]} maxBarSize={28} />
                          <Bar yAxisId="right" dataKey="util"    name="Utilization %"    radius={[3,3,0,0]} maxBarSize={28}>
                            {utilChartData.map((e, i) => (
                              <Cell key={i} fill={e.util>=70 ? C.green : e.util>=30 ? C.accent : C.yellow} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Search */}
                  <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <SearchBar
                      value={utilSearch}
                      onChange={v => { setUtilSearch(v); setUtilPage(1); }}
                      placeholder="Search teacher name…"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {filteredUtil.length} result{filteredUtil.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {/* Table */}
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TH width={44}>#</TH>
                          <TH>Teacher</TH>
                          <TH align="center" width={80}>Subjects</TH>
                          <TH align="center" width={80}>Classes</TH>
                          <TH align="center" width={100}>Assigned</TH>
                          <TH align="center" width={100}>Total</TH>
                          <TH align="center" width={80}>Free</TH>
                          <TH width={190}>Utilization</TH>
                          <TH align="center" width={90}>Category</TH>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedUtil.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} align="center" sx={{ py: 5, color: C.muted }}>
                              No teachers found
                            </TableCell>
                          </TableRow>
                        ) : pagedUtil.map((t, idx) => {
                          const rowNum = (utilPage - 1) * utilRows + idx + 1;
                          const util   = t.utilizationPercent || 0;
                          return (
                            <TableRow key={t.teacherId} hover sx={{ '&:last-child td': { border: 0 } }}>
                              <TableCell sx={{ color: C.muted, fontSize: '0.72rem' }}>{rowNum}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                                  <Avatar sx={{ width: 30, height: 30, bgcolor: C.primary, fontSize: '0.72rem', fontWeight: 700 }}>
                                    {t.teacherName?.charAt(0)?.toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: C.primary }}>
                                    {t.teacherName}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center"><Typography variant="body2">{t.subjectCount || 0}</Typography></TableCell>
                              <TableCell align="center"><Typography variant="body2">{t.classCount || 0}</Typography></TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 700, color: C.primary }}>{t.assignedPeriods || 0}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color="text.secondary">{t.availablePeriods || 0}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" color="text.secondary">
                                  {(t.availablePeriods || 0) - (t.assignedPeriods || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell><UtilBar value={util} /></TableCell>
                              <TableCell align="center"><StatusChip status={t.loadCategory} /></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePager
                    total={filteredUtil.length}
                    page={utilPage}
                    rowsPerPage={utilRows}
                    onPage={setUtilPage}
                    onRows={setUtilRows}
                  />
                </>
              )}
            </>
          )}

          {/* ================================================================
              TAB 1 — TEACHER DETAIL (card layout)
          ================================================================ */}
          {tab === 1 && (
            <CardContent sx={{ p: 2.5 }}>
              {/* Teacher selector */}
           <Grid container spacing={2} sx={{ mb: 3 }}>
  <Grid item xs={12} sm={5}>
    <FormControl fullWidth size="small">
      <InputLabel>Select Teacher</InputLabel>
      <Select
        value={selTeacher}
        label="Select Teacher"
        onChange={e => setSelTeacher(e.target.value)}
      >
        {summaryList.map(t => (
          <MenuItem key={t.teacherId} value={t.teacherId}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 22, height: 22, bgcolor: C.primary, fontSize: '0.65rem' }}>
                {t.teacherName?.charAt(0)}
              </Avatar>
              {t.teacherName}
              {t.isClassTeacher === 'Yes' && (
                <Chip label="CT" size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700, ml: 0.5 }} />
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Grid>
</Grid>

              {!selTeacher ? (
                <Paper sx={{ py: 7, textAlign: 'center', bgcolor: '#F8FAFC', border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                  <PersonIcon sx={{ fontSize: 52, color: '#CBD5E1', mb: 1 }} />
                  <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                    Select a teacher to view their timetable detail
                  </Typography>
                </Paper>
              ) : loadingDetail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress sx={{ color: C.primary }} />
                </Box>
              ) : (
                <>
                  {/* KPI cards from /summary */}
                  {selTeacherSummary && (
                    <Grid container spacing={1.5} sx={{ mb: 3 }}>
                      {[
                        { label: 'Total Assigned',  value: selTeacherSummary.totalAssignedPeriods  || 0,                          color: C.accent,   icon: <CalendarIcon sx={{ fontSize: 16 }} /> },
                        { label: 'Total',       value: selTeacherSummary.totalAvailablePeriods || 0,                         color: C.muted,    icon: <ClockIcon    sx={{ fontSize: 16 }} /> },
                        { label: 'Free Periods',    value: selTeacherSummary.freePeriods           || 0,                          color: C.green,    icon: <CheckIcon    sx={{ fontSize: 16 }} /> },
                        { label: 'Utilization',     value: `${(selTeacherSummary.utilizationPercent||0).toFixed(1)}%`,             color: '#7B1FA2',  icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
                        { label: 'Load Status',     value: selTeacherSummary.loadStatus,                                          color: C.orange,   icon: <WarnIcon     sx={{ fontSize: 16 }} /> },
                        { label: 'Class Teacher',   value: selTeacherSummary.isClassTeacher,                                      color: '#00897B',  icon: <SchoolIcon   sx={{ fontSize: 16 }} /> },
                      ].map((s, i) => (
                        <Grid item xs={6} sm={4} md={2} key={i}>
                          <Paper sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${C.border}`, textAlign: 'center', boxShadow: 'none' }}>
                            <Box sx={{ color: s.color, mb: 0.2 }}>{s.icon}</Box>
                            <Typography variant="caption" sx={{ fontSize: '0.66rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                              {s.label}
                            </Typography>
                            <Typography sx={{ fontWeight: 800, color: s.color, lineHeight: 1.2, mt: 0.2, fontSize: '0.95rem' }}>
                              {s.value || '—'}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* Stats banner */}
                  <Box sx={{
                    display: 'flex', gap: 2, mb: 2.5, p: 2,
                    bgcolor: '#F0F4FF', borderRadius: 2,
                    border: `1px solid #C7D7F9`,
                    flexWrap: 'wrap', alignItems: 'center',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.accent }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: C.primary }}>
                        {uniqueSlotCount} unique timetable slots
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.green }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: C.primary }}>
                        {subjectGroups.length} subject–class combination{subjectGroups.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.yellow }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: C.primary }}>
                        {[...new Set(subjectGroups.map(g => g.className))].length} class{[...new Set(subjectGroups.map(g => g.className))].length !== 1 ? 'es' : ''}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Search + per-page controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <SearchBar
                      value={cardSearch}
                      onChange={v => { setCardSearch(v); setCardPage(1); }}
                      placeholder="Search subject, class or section…"
                      width={300}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">Cards per page:</Typography>
                      <Select
                        value={cardsPerPage}
                        onChange={e => { setCardsPerPage(Number(e.target.value)); setCardPage(1); }}
                        size="small"
                        sx={{ fontSize: '0.78rem', height: 28, minWidth: 62 }}
                      >
                        {ROW_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                      </Select>
                    </Box>
                  </Box>

                  {/* Cards grid */}
                  {filteredGroups.length === 0 ? (
                    <Paper sx={{ py: 6, textAlign: 'center', bgcolor: '#F8FAFC', border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                      <Typography color="text.secondary">
                        {subjectGroups.length === 0
                          ? 'No timetable slots found for this teacher.'
                          : 'No results match your search.'}
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        {pagedGroups.map(group => (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={group.key}>
                            <SubjectCard group={group} />
                          </Grid>
                        ))}
                      </Grid>

                      {/* Card pagination */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2.5, pt: 1.5, borderTop: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Showing {Math.min((cardPage-1)*cardsPerPage+1, filteredGroups.length)}–{Math.min(cardPage*cardsPerPage, filteredGroups.length)} of {filteredGroups.length} subject groups
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => setCardPage(p => p-1)} disabled={cardPage <= 1}>
                            <PrevIcon fontSize="small" />
                          </IconButton>
                          {Array.from({ length: Math.ceil(filteredGroups.length / cardsPerPage) }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === Math.ceil(filteredGroups.length/cardsPerPage) || Math.abs(p-cardPage) <= 1)
                            .reduce((acc, p, i, arr) => {
                              if (i > 0 && p - arr[i-1] > 1) acc.push('…');
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, i) => p === '…' ? (
                              <Typography key={i} sx={{ px: 0.5, color: C.muted, lineHeight: '30px', fontSize: '0.8rem' }}>…</Typography>
                            ) : (
                              <IconButton
                                key={p}
                                size="small"
                                onClick={() => setCardPage(p)}
                                sx={{
                                  width: 30, height: 30, fontSize: '0.78rem', fontWeight: 700,
                                  bgcolor: p === cardPage ? C.primary : 'transparent',
                                  color:   p === cardPage ? '#fff'     : C.muted,
                                  '&:hover': { bgcolor: p === cardPage ? C.navy : '#F1F5F9' },
                                }}
                              >
                                {p}
                              </IconButton>
                            ))
                          }
                          <IconButton size="small" onClick={() => setCardPage(p => p+1)} disabled={cardPage >= Math.ceil(filteredGroups.length/cardsPerPage)}>
                            <NextIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </>
                  )}
                </>
              )}
            </CardContent>
          )}

          {/* ================================================================
              TAB 2 — CLASS SUBJECT SUMMARY
          ================================================================ */}
          {tab === 2 && (
            <CardContent sx={{ p: 2.5 }}>
              {/* Selectors */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Class</InputLabel>
                    <Select value={selClass} label="Class" onChange={e => setSelClass(e.target.value)}>
                      {classes.map(c => (
                        <MenuItem key={c.classId||c.ClassId||c.classesId} value={c.classId||c.ClassId||c.classesId}>
                          {c.className||c.ClassName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small" disabled={!selClass}>
                    <InputLabel>Section</InputLabel>
                    <Select value={selSection} label="Section" onChange={e => setSelSection(e.target.value)}>
                      {sections.map(s => (
                        <MenuItem key={s.sectionId||s.SectionId} value={s.sectionId||s.SectionId}>
                          {s.sectionName||s.SectionName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {!selClass || !selSection ? (
                <Paper sx={{ py: 7, textAlign: 'center', bgcolor: '#F8FAFC', border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                  <SchoolIcon sx={{ fontSize: 52, color: '#CBD5E1', mb: 1 }} />
                  <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                    Select a class and section to view subject summary
                  </Typography>
                </Paper>
              ) : loadingClass ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress sx={{ color: C.primary }} />
                </Box>
              ) : (
                <>
                  {/* Status pills */}
                  {mergedClass.length > 0 && (
                    <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                      {[
                        { label: 'Total Subjects', value: mergedClass.length,                                                               color: C.accent  },
                        { label: 'On Track',        value: mergedClass.filter(r=>r.status==='On Track'||r.status==='Met').length,            color: C.green   },
                        { label: 'Behind',          value: mergedClass.filter(r=>r.status==='Behind' ||r.status==='Under').length,           color: C.red     },
                        { label: 'Over',            value: mergedClass.filter(r=>r.status==='Over').length,                                  color: C.orange  },
                      ].map((p, i) => (
                        <Paper key={i} sx={{ px: 2, py: 1, borderRadius: 2, border: `1px solid ${C.border}`, display: 'flex', gap: 1, alignItems: 'center', boxShadow: 'none' }}>
                          <Typography sx={{ fontWeight: 900, color: p.color, fontSize: '1.05rem' }}>{p.value}</Typography>
                          <Typography variant="caption" sx={{ color: C.muted, fontWeight: 600 }}>{p.label}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  )}

                  {/* Chart */}
                  {classChartData.length > 0 && (
                    <Box sx={{ mb: 3, height: 200 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: C.primary, mb: 1 }}>
                        Target vs Actual Periods per Subject
                      </Typography>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={classChartData} margin={{ left: -15, right: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <RTooltip />
                          <Legend />
                          <Bar dataKey="Target" fill="#90CAF9" radius={[3,3,0,0]} maxBarSize={28} />
                          <Bar dataKey="Actual" fill={C.accent}  radius={[3,3,0,0]} maxBarSize={28} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Search */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <SearchBar
                      value={classSearch}
                      onChange={v => { setClassSearch(v); setClassPage(1); }}
                      placeholder="Search subject or teacher…"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {filteredClass.length} subject{filteredClass.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>

                  {mergedClass.length === 0 ? (
                    <Paper sx={{ py: 5, textAlign: 'center', bgcolor: '#F8FAFC', border: `1px dashed ${C.border}`, borderRadius: 2 }}>
                      <Typography color="text.secondary">
                        No subject targets found. Add them in the Subject Targets page first.
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      <TableContainer sx={{ maxHeight: 400, border: `1px solid ${C.border}`, borderRadius: 2 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TH width={44}>#</TH>
                              <TH>Subject</TH>
                              <TH>Teacher</TH>
                              <TH align="center" width={110}>Target / Week</TH>
                              <TH align="center" width={90}>Actual</TH>
                              <TH align="center" width={100}>Difference</TH>
                              <TH align="center" width={100}>Status</TH>
                              <TH width={150}>Progress</TH>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {pagedClass.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5, color: C.muted }}>
                                  No results match your search.
                                </TableCell>
                              </TableRow>
                            ) : pagedClass.map((r, idx) => {
                              const rowNum   = (classPage - 1) * classRows + idx + 1;
                              const pct      = r.targetPeriods > 0 ? (r.actualPeriods / r.targetPeriods) * 100 : 0;
                              const barColor =
                                (r.status === 'On Track' || r.status === 'Met') ? C.green :
                                r.status === 'Over' ? C.orange : C.red;
                              const diff     = r.difference ?? (r.actualPeriods - r.targetPeriods);
                              return (
                                <TableRow key={r.subjectId} hover sx={{ '&:last-child td': { border: 0 } }}>
                                  <TableCell sx={{ color: C.muted, fontSize: '0.72rem' }}>{rowNum}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: C.primary }}>{r.subjectName}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ color: C.muted }}>{r.teacherName}</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="body2" color="text.secondary">{r.targetPeriods}</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: C.primary }}>{r.actualPeriods}</Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 700, color: diff === 0 ? C.green : diff > 0 ? C.orange : C.red }}
                                    >
                                      {diff > 0 ? `+${diff}` : diff}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center"><StatusChip status={r.status} /></TableCell>
                                  <TableCell>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(pct, 100)}
                                      sx={{ height: 7, borderRadius: 3, bgcolor: '#EEF2F7', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: barColor } }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePager
                        total={filteredClass.length}
                        page={classPage}
                        rowsPerPage={classRows}
                        onPage={setClassPage}
                        onRows={setClassRows}
                      />
                    </>
                  )}
                </>
              )}
            </CardContent>
          )}

        </Card>
      </Container>
    </Box>
  );
}